import { Component, Input, Output, EventEmitter, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { SessionService } from '../core/session.service';
import { PassportHistoryResponse } from '../interfaces/api-responses';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { DateTimeHelperService } from '../shared/date-time-helper.service';

export interface HistoryItem {
  historyID: number;
  passportID: number;
  oldValues: string;
  newValues: string;
  reason: string;
  createdBy: number;
  createdByUsername: string;
  createdByRoles: string;
  createdOn: string;
  action: string;
  roleID?: number;
  roleName?: string;
  submittedBy?: number;
  submittedDateTime?: string;
  updatedDateTime?: string;
  remarks?: string;
  shuntingYardID?: number;
  haccpChange?: boolean;
  hasHaccpUpload?: boolean;
  // NEW: From backend response
  createdByID?: number;
  createdByEmployeeName?: string;
  details: HistoryDetail[];
}

export interface HistoryDetail {
  historyId: number;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  isDraftStatus?: boolean;
  isSubmitStatus?: boolean;
  isHaccpUpload?: boolean;
}

export interface HistoryHeaderData {
  loadNumber?: string;
  scheduledText?: string;
  variety?: string;
  haulier?: string;
  origin?: string;
  destination?: string;
  apnName?: string;
  vendorName?: string;
  vendorNumber?: string;
  bulkerNumber?: string;
}

@Component({
  selector: 'app-history',
  standalone: true,
  imports: [CommonModule, FormsModule, DateWithOrdinalPipe],
  templateUrl: './history.component.html',
  styleUrl: './history.component.css'
})
export class HistoryComponent implements OnInit {
  @Input() passportId: string | null = null;
  @Input() header: HistoryHeaderData | null = null;
  @Input() title: string = 'Passport History';
  @Output() closed = new EventEmitter<void>();

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly detailsStore = inject(PassportDetailsStore);
  private readonly dateTimeHelper = inject(DateTimeHelperService);

  items = signal<HistoryItem[]>([]);
  isLoading = signal(false);
  query = signal('');
  isOnline = signal<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);

  ngOnInit(): void {
    this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    // pick up navigation state from router state or history
    const stateFromHistory = history?.state || undefined;
    const state = stateFromHistory;
    if (state) {
      if (!this.passportId && state.passportId) this.passportId = state.passportId;
      if (!this.header && state.header) this.header = state.header as HistoryHeaderData;
    }
    
    // Check and mark PCR changes as read if needed
    await this.checkAndMarkPcrChangesAsRead();
    
    await this.loadHistory();
  }

  get isTransloading(): boolean {
    return this.session.isTransloading();
  }

  get isGatehouse(): boolean {
    return this.session.isGatehouse() || (this.header as any)?.isGatehouse === true;
  }

  get isScheduler(): boolean {
    return this.session.isScheduler() || (this.header as any)?.isScheduler === true;
  }

  get isReceiver(): boolean {
    return this.session.isReceiver() || (this.header as any)?.isReceiver === true;
  }

  get isShunting(): boolean {
    return this.session.isShunting() || (this.header as any)?.isShunting === true;
  }

  get headerVariety(): string { return this.header?.variety || ''; }
  get headerVendorName(): string { return this.header?.vendorName || ''; }
  get headerVendorNumber(): string { return this.header?.vendorNumber || ''; }
  get headerBulkerNumber(): string { return this.header?.bulkerNumber || ''; }
  get headerOrigin(): string { return this.header?.origin || ''; }
  get headerHaulier(): string { return this.header?.haulier || ''; }

  private createDraftStatusEntry() {
    return {
      historyId: 0,
      fieldName: 'Status',
      oldValue: null,
      newValue: 'Saved as Draft',
      isDraftStatus: true
    };
  }

  private createSubmissionStatusEntry() {
    return {
      historyId: 0,
      fieldName: 'Passport',
      oldValue: 'Draft',
      newValue: 'Finished',
      isSubmitStatus: true
    };
  }

  private getRoleIdForPersona(): number {
    const persona = (this.session.persona() || '').toLowerCase();
    const roleMap: Record<string, number> = {
      'auditor': 8,
      'audit': 8,
      'admin': 1,
      'grower': 2,
      'transloader': 3,
      'receiver': 4,
      'shunter': 5,
      'shunting': 5,
      'scheduler': 6,
      'gate house': 7,
      'gatehouse': 7
    };
    
    // Check for specific matches first
    for (const [key, value] of Object.entries(roleMap)) {
      if (persona.includes(key)) {
        return value;
      }
    }
    
    return 2; // Default to Grower
  }

  private async checkAndMarkPcrChangesAsRead(): Promise<void> {
    const passportId = this.passportId;
    if (!passportId) {
      console.log('No passport ID available for PCR check');
      return;
    }

    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    
    if (!token || !this.isOnline()) {
      console.log('Cannot mark PCR as read: no token or offline');
      return;
    }

    try {
      const roleId = this.getRoleIdForPersona();
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/pcr-change/mark-read?passportId=${passportId}&roleId=${roleId}`;
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };

      console.log('Marking PCR changes as read for passport', passportId, 'role', roleId);
      
      await firstValueFrom(
        this.http.post(apiUrl, {}, { headers, withCredentials: true })
      );
      
      // Update local store to mark as seen
      this.detailsStore.update(passportId, { hasSeenPcrChanges: true });
      
      console.log('✅ Successfully marked PCR changes as read for passport', passportId);
    } catch (error) {
      console.warn('Failed to mark PCR changes as read:', error);
      // Don't block the user experience - just log the error
    }
  }

  async loadHistory(): Promise<void> {
    const userID = localStorage.getItem('userID');
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const passportId = this.passportId || '';
    
    if (!token || !userID || !passportId) {
      console.warn('Missing required data for loading history:', { hasToken: !!token, hasUserID: !!userID, hasPassportId: !!passportId });
      // Try to load from offline storage
      await this.loadFromOfflineStorage(passportId);
      return;
    }
    
    // Check if offline - load from IndexedDB
    if (!this.isOnline()) {
      console.log('Device is offline - loading history from IndexedDB');
      await this.loadFromOfflineStorage(passportId);
      return;
    }
    
    // Use the correct endpoint format: GET /api/v1/passports/history?userId={userId}&passportId={passportId}
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/history?userId=${userID}&passportId=${passportId}`;
    const headers = { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' };
    
    console.log('Loading passport history from:', apiUrl);
    this.isLoading.set(true);
    
    try {
      const response = await firstValueFrom(this.http.get<PassportHistoryResponse[]>(apiUrl, { headers }));
      console.log('History API raw response:', response);
      
      // Log actions for debugging
      if (response && response.length > 0) {
        console.log('History actions received:', response.map(item => ({
          action: item.action,
          historyID: item.historyID,
          createdByUsername: item.createdByUsername,
          createdByRoles: item.createdByRoles
        })));
      }
      
      if (response && response.length > 0) {
        // Transform the backend response to match our frontend format
        const transformedHistory = this.transformHistoryData(response);
        // Sort by createdOn descending (latest first)
        const sortedHistory = this.sortHistoryByCreatedOn(transformedHistory);
        console.log('Transformed history (after filtering):', sortedHistory.length, 'records');
        console.log('All history items:', sortedHistory.map((h: any) => ({
          historyID: h.historyID,
          action: h.action,
          createdOn: h.createdOn,
          detailsCount: h.details.length,
          details: h.details.map((d: any) => d.fieldName)
        })));
        this.items.set(sortedHistory);
      } else {
        // No history found, show empty state
        this.items.set([]);
      }
    } catch (error: any) {
      console.warn('Failed to load history:', error);
      if (error?.status === 404) {
        // No history found is not an error, just set empty array
        this.items.set([]);
      } else {
        // Try loading from offline storage on error
        await this.loadFromOfflineStorage(passportId);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Load history from offline storage (IndexedDB)
   */
  private async loadFromOfflineStorage(passportId: string): Promise<void> {
    this.isLoading.set(true);
    try {
      // First check if history is in detailsStore
      const details = this.detailsStore.get(passportId);
      if (details?.history && details.history.length > 0) {
        console.log(`Loaded ${details.history.length} history items from detailsStore`);
        const transformedHistory = this.transformHistoryData(details.history);
        // Sort by createdOn descending (latest first)
        const sortedHistory = this.sortHistoryByCreatedOn(transformedHistory);
        this.items.set(sortedHistory);
        return;
      }

      // Otherwise try IndexedDB
      const cachedHistory = await this.offlineStorage.getHistory(Number.parseInt(passportId));
      if (cachedHistory && cachedHistory.length > 0) {
        console.log(`Loaded ${cachedHistory.length} history items from offline storage`);
        const transformedHistory = this.transformHistoryData(cachedHistory);
        // Sort by createdOn descending (latest first)
        const sortedHistory = this.sortHistoryByCreatedOn(transformedHistory);
        this.items.set(sortedHistory);
      } else {
        console.log('No cached history found');
        this.items.set([]);
      }
    } catch (error) {
      console.warn('Error loading history from offline storage:', error);
      this.items.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  private transformHistoryData(backendData: any[]): HistoryItem[] {
    // Filter out SUBMISSION OMITTED and OVERRIDDEN BY OFFLINE records
    const filteredData = backendData.filter(item => {
      const action = (item.action || item.Action || '').toUpperCase();
      return action !== 'SUBMISSION OMITTED' &&
        action !== 'OVERRIDDEN BY OFFLINE' &&
        action !== 'SENT TO RECEIVING';
    });
    
    return filteredData.map(item => {
      // Parse the details array if it exists
      let transformedDetails = item.details || item.Details || [];
      
      // If details are not provided but we have old/new values, parse them
      if (transformedDetails.length === 0 && (item.oldValues || item.newValues || item.OldValues || item.NewValues)) {
        transformedDetails = this.parseValuesIntoDetails(
          item.oldValues || item.OldValues,
          item.newValues || item.NewValues
        );
      }
      
      // FIRST: Filter out unwanted fields by raw field name (before transformation)
      transformedDetails = transformedDetails.filter((detail: any) => {
        const rawFieldName = detail.fieldName || detail.FieldName || '';
        const rawNormalized = rawFieldName.toLowerCase().replaceAll(/\s|_/g, '');
        
        // Filter out these specific fields by their raw backend names
        if (rawFieldName === 'AuthorisedByUserID' || 
            rawFieldName === 'AuthorizedByUserID' ||
            rawNormalized === 'authorisedbyuserid' ||
            rawNormalized === 'authorizedbyuserid' ||
            rawFieldName === 'TrailerMain' ||
            rawNormalized === 'trailermain' ||
            rawFieldName === 'BulkerMain' ||
            rawNormalized === 'bulkermain' ||
            rawFieldName === 'OriginID' ||
            rawNormalized === 'originid' ||
            rawFieldName === 'ShuntingYardID' ||
            rawNormalized === 'shuntingyardid' ||
            rawFieldName === 'RawSourceType' ||
            rawNormalized === 'rawsourcetype' ||
            rawFieldName === 'Raw Source Type') {
          return false;
        }
        return true;
      });
      
      // Transform each detail to ensure proper formatting
      transformedDetails = transformedDetails.map((detail: any) => this.transformFieldDetail(detail))
        .filter((detail: any) => detail !== null); // Remove null entries (filtered HACCP entries)

      // Determine if any HACCP upload entries exist in this history item
      const hasHaccpUpload = transformedDetails?.some((d: any) => d?.isHaccpUpload === true);
      
      // Filter out Planned Date/Time fields
      // Filter out unwanted fields (Planned Date/Time, BulkerMain, TrailerMain, AuthorisedByUserID, OriginID)
      transformedDetails = transformedDetails.filter((detail: any) => {
        if (!detail?.fieldName) {
          return true;
        }
        const fieldName = detail.fieldName;
        const fieldNameLower = fieldName.toLowerCase();
        const normalized = fieldNameLower.replaceAll(/\s|_/g, '');
        // Remove Planned Date and Timing fields
        if (fieldNameLower.includes('planned') || 
            fieldName === 'PlannedDateTime' ||
            fieldName === 'Planned Date' ||
            fieldName === 'Planned Time' ||
            fieldName === 'Planned Date & Time') {
          return false;
        }
        // Remove BulkerMain, TrailerMain, AuthorisedByUserID, and OriginID fields
        if (fieldName === 'BulkerMain' || 
          fieldName === 'Bulker Main' ||
          fieldName === 'TrailerMain' ||
          fieldName === 'Trailer Main' ||
          normalized === 'trailermain' ||
          fieldName === 'AuthorisedByUserID' || 
          fieldName === 'Authorised By User ID' ||
          fieldName === 'Authorised By UserID' ||
          fieldName === 'AuthorizedByUserID' ||
          fieldName === 'Authorized By User ID' ||
          normalized === 'authorisedbyuserid' ||
          normalized === 'authorizedbyuserid' ||
          fieldName === 'OriginID' ||
          fieldName === 'Origin ID' ||
          normalized === 'originid') {
        return false;
      }
        // Remove ShuntingYardID changes (any variant like "Shunting Yard ID" or with spaces)
        if (normalized === 'shuntingyardid') {
          return false;
        }
        // Filter out spurious Shunting Yard name changes where the value didn't actually change
        // (e.g. NULL/"N/A" → "No Shunting Yard Assigned" when the yard was never touched)
        if (fieldName === 'Shunting Yard' || normalized === 'shuntingyardname' || normalized === 'shuntingyard') {
          const ov = (detail.oldValue ?? detail.OldValue ?? '').toString().toLowerCase().trim();
          const nv = (detail.newValue ?? detail.NewValue ?? '').toString().toLowerCase().trim();
          const noYardValues = new Set(['n/a', 'null', '', 'no shunting yard assigned', 'no shunting yard selected', 'no shunting required']);
          if (noYardValues.has(ov) && noYardValues.has(nv)) {
            return false;
          }
        }
        // Remove RawSourceType (internal metadata, origin changes tracked via Origin Field)
        if (fieldName === 'RawSourceType' ||
            fieldName === 'Raw Source Type' ||
            normalized === 'rawsourcetype') {
          return false;
        }
        return true;
      });
      
      // Add special status entries based on action type
      transformedDetails = this.addStatusEntry(item, transformedDetails);
      
      // Debug logging for SUBMITTED DIRECTLY
      if (item.action === 'SUBMITTED DIRECTLY') {
        console.log('SUBMITTED DIRECTLY after addStatusEntry:', transformedDetails.length, 'details:', transformedDetails.map((d: any) => d.fieldName));
      }
      
      // Extract createdBy info - NEW: Use createdByEmployeeName and roleName from backend
      const createdByEmployeeName = item.createdByEmployeeName || item.CreatedByEmployeeName || '';
      const roleName = item.roleName || item.RoleName || '';
      
      // Fallback to old fields if new ones are not available
      const createdByUsername = createdByEmployeeName || item.createdByUsername || item.CreatedByUsername || 'Unknown User';
      const createdByRoles = roleName || item.createdByRoles || item.CreatedByRoles || '';
      
      return {
        historyID: item.historyID || item.HistoryID || 0,
        passportID: item.passportID || item.PassportID || 0,
        oldValues: item.oldValues || item.OldValues || '',
        newValues: item.newValues || item.NewValues || '',
        reason: item.reason || item.Reason || item.remarks || item.Remarks || '',
        createdBy: item.createdBy || item.CreatedBy || 0,
        createdByUsername: createdByUsername,
        createdByRoles: createdByRoles,
        createdOn: this.getUtcTimestamp(item.createdOn || item.CreatedOn) || new Date().toISOString(),
        action: item.action || item.Action || '',
        roleID: item.roleID || item.RoleID,
        roleName: roleName,
        submittedBy: item.submittedBy || item.SubmittedBy,
        submittedDateTime: this.getUtcTimestamp(item.submittedDateTime || item.SubmittedDateTime),
        updatedDateTime: this.getUtcTimestamp(item.updatedDateTime || item.UpdatedDateTime),
        remarks: item.remarks || item.Remarks,
        shuntingYardID: item.shuntingYardID || item.ShuntingYardID,
        haccpChange: item.haccpChange ?? item.HaccpChange ?? false,
        hasHaccpUpload,
        // NEW: Map the new backend fields
        createdByID: item.createdByID || item.CreatedByID,
        createdByEmployeeName: createdByEmployeeName,
        details: transformedDetails
      };
    });
  }

  /**
   * Sort history items by createdOn descending (latest first)
   */
  private sortHistoryByCreatedOn(historyItems: HistoryItem[]): HistoryItem[] {
    return [...historyItems].sort((a, b) => {
      const dateA = new Date(a.createdOn || 0).getTime();
      const dateB = new Date(b.createdOn || 0).getTime();
      return dateB - dateA; // Descending order (latest first)
    });
  }
  
  private parseValuesIntoDetails(oldValues: string, newValues: string): any[] {
    const details: any[] = [];
    try {
      const oldObj = oldValues ? JSON.parse(oldValues) : {};
      const newObj = newValues ? JSON.parse(newValues) : {};
      
      // Get all unique keys from both objects
      const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
      
      for (const key of allKeys) {
        const oldVal = oldObj[key];
        const newVal = newObj[key];
        
        // Only add if there's an actual change
        if (oldVal !== newVal) {
          details.push({
            fieldName: this.formatFieldName(key),
            oldValue: oldVal,
            newValue: newVal
          });
        }
      }
    } catch (e) {
      console.warn('Failed to parse history values:', e);
    }
    return details;
  }
  
  private addStatusEntry(item: any, details: any[]): any[] {
    console.log('Processing history action:', item.action || item.Action || '');

    const result = this.resolveStatusEntry(item, details);
    if (!result) return details;

    const filteredDetails = result.details.filter((d: any) =>
      d.fieldName !== 'Status' &&
      d.fieldName !== 'Passport' &&
      !d.isDraftStatus &&
      !d.isSubmitStatus
    );
    return [result.entry, ...filteredDetails];
  }

  private resolveStatusEntry(item: any, details: any[]): { entry: any; details: any[] } | null {
    const action = (item.action || item.Action || '');
    const actionLower = action.toLowerCase();
    const oldValues = item.oldValues || item.OldValues || '';
    const newValues = item.newValues || item.NewValues || '';

    if (this.isPassportCreatedAction(actionLower)) {
      return { entry: { historyId: 0, fieldName: 'Status', oldValue: null, newValue: 'Load request created by the scheduler.', isSubmitStatus: true }, details };
    }
    if (this.isDraftSavedAction(actionLower)) {
      return { entry: this.createDraftStatusEntry(), details };
    }
    if (this.isSubmittedDirectlyAction(actionLower)) {
      return { entry: { historyId: 0, fieldName: 'Status', oldValue: null, newValue: 'Submitted Directly', isSubmitStatus: true }, details };
    }
    if (this.isShuntingYardAction(actionLower)) {
      return this.handleShuntingYardAssigned(item, details);
    }
    if (this.isDraftSubmittedAction(actionLower, oldValues, newValues)) {
      return { entry: this.createSubmissionStatusEntry(), details };
    }
    return null;
  }

  private isPassportCreatedAction(actionLower: string): boolean {
    return actionLower === 'passport created';
  }

  private isDraftSavedAction(actionLower: string): boolean {
    return actionLower === 'draft saved' ||
      (actionLower.includes('draft') && actionLower.includes('save'));
  }

  private isSubmittedDirectlyAction(actionLower: string): boolean {
    return actionLower === 'submitted directly';
  }

  private isShuntingYardAction(actionLower: string): boolean {
    return actionLower === 'shunting yard assigned';
  }

  private isDraftSubmittedAction(actionLower: string, oldValues: string, newValues: string): boolean {
    return actionLower === 'draft submitted' ||
      (actionLower.includes('draft') && (actionLower.includes('submit') || actionLower.includes('submitted') || actionLower.includes('finish'))) ||
      (oldValues?.includes('Draft') && newValues?.includes('Finished')) ||
      (oldValues?.includes('"Status":"Draft"') && newValues?.includes('"Status":"Finished"'));
  }

  private handleShuntingYardAssigned(item: any, details: any[]): { entry: any; details: any[] } {
    const shuntingYardID = item.shuntingYardID || item.ShuntingYardID;
    const hasYardAssigned = shuntingYardID !== null && shuntingYardID !== undefined && shuntingYardID !== 0;
    let message = 'No Shunting Yard Selected';
    let updatedDetails = details;

    if (hasYardAssigned) {
      message = this.resolveYardName(item, details);
    } else {
      updatedDetails = details.filter((d: any) => d.fieldName !== 'Shunting Yard');
    }

    const entry = { historyId: 0, fieldName: 'Status', oldValue: null, newValue: message, isSubmitStatus: true };
    return { entry, details: updatedDetails };
  }

  private resolveYardName(item: any, details: any[]): string {
    let yardName = '';
    try {
      const newVals = JSON.parse(item.newValues || item.NewValues || '{}');
      yardName = newVals.ShuntingYardName || '';
    } catch (error_) {
      console.warn('Failed to parse shunting yard values:', error_);
    }
    if (!yardName) {
      const yardDetail = details.find((d: any) => d.fieldName === 'Shunting Yard');
      if (yardDetail?.newValue && yardDetail.newValue !== 'N/A') {
        yardName = yardDetail.newValue;
      }
    }
    return yardName ? `${yardName} Assigned` : 'Shunting Yard Assigned';
  }
  
  private formatFieldName(key: string): string {
    // Special formatting for known fields (check before general formatting)
    const fieldMap: Record<string, string> = {
      'TrailerNumber': 'Bulker Number',
      'BulkerNumber': 'Bulker Number',
      'LoadingDateTime': 'Loading Date & Time',
      'OriginField': 'Origin Field',
      'OriginID': 'Origin Field',
      'OriginName': 'Origin Field',
      'AuthorisedBy': 'Authorised By',
      'AuthorisedByUserID': 'Authorised By',
      'AuthorizerName': 'Authorised By',
      'AuthorizedBy': 'Authorised By',
      'AuthorizedByUserID': 'Authorised By',
      'HaccpCheck': 'HACCP Image',
      'HACCPImage': 'HACCP Image',
      'TransloadingStation': 'Transloading Station',
      'ShuntingYardName': 'Shunting Yard',
      'Destination': 'Plant',
      'DestinationWeighbridge': 'Destination'
    };
    
    // Return mapped value if exists
    if (fieldMap[key]) {
      return fieldMap[key];
    }
    
    // Convert camelCase or PascalCase to readable format with proper word separation
    // Use a safer regex pattern to prevent ReDoS (Regular Expression Denial of Service)
    const formatted = key
      // Add space before capital letters (including consecutive capitals)
      // Use a more constrained pattern to prevent backtracking: match 1-50 capitals max
      .replaceAll(/([A-Z]{1,50})(?=[A-Z][a-z])/g, '$1 ')  // Handle consecutive capitals like "ID" in "UserID"
      .replaceAll(/([a-z\d])([A-Z])/g, '$1 $2')      // Handle normal camelCase
      .replaceAll(/([a-zA-Z])(\d)/g, '$1 $2')        // Add space before numbers
      .replaceAll(/(\d)([a-zA-Z])/g, '$1 $2')        // Add space after numbers
      // Capitalize first letter
      .replace(/^./, str => str.toUpperCase())
      .trim();
    
    return formatted;
  }
  
  private transformFieldDetail(detail: any): any {
    const rawFieldName = detail.fieldName || detail.FieldName || '';
    // Apply formatting to ensure proper word separation
    const fieldName = this.formatFieldName(rawFieldName);
    let oldValue = detail.oldValue ?? detail.OldValue;
    let newValue = detail.newValue ?? detail.NewValue;
    
    // Try to get names from the detail object if they exist
    const oldName = detail.oldName || detail.OldName;
    const newName = detail.newName || detail.NewName;
    
    // Format values based on field type
    if (fieldName === 'Authorised By' || fieldName.includes('Authorised') || fieldName.includes('Authorized')) {
      // For Authorised By field, use names if available, otherwise format IDs
      oldValue = oldName || this.formatAuthorizedBy(oldValue, detail);
      newValue = newName || this.formatAuthorizedBy(newValue, detail);
    } else if (fieldName === 'Origin Field' || fieldName.includes('Origin')) {
      // For Origin Field, use names if available, otherwise format IDs
      oldValue = oldName || this.formatOriginField(oldValue, detail);
      newValue = newName || this.formatOriginField(newValue, detail);
    } else if (fieldName.includes('Date') || fieldName.includes('Time')) {
      // Format date/time fields
      oldValue = this.formatDateTime(oldValue);
      newValue = this.formatDateTime(newValue);
    }
    
    // Convert null/undefined/empty to "N/A"
    if (oldValue === null || oldValue === undefined || oldValue === '' || oldValue === 'null') {
      oldValue = 'N/A';
    }
    if (newValue === null || newValue === undefined || newValue === '' || newValue === 'null') {
      newValue = 'N/A';
    }
    
    // Special handling for HACCP fields - always show as "uploaded" without old/new values
    if (fieldName === 'HACCP Image' || fieldName === 'HaccpCheck' || fieldName.includes('HACCP')) {
      // Check if there's a meaningful change indicating upload
      if (newValue === true || newValue === 'true' || newValue === '1' || 
          newValue === 'uploaded' || (newValue && newValue !== 'N/A' && newValue !== 'false' && newValue !== '0')) {
        return {
          historyId: detail.historyId || detail.HistoryId || 0,
          fieldName: 'HACCP Image',
          oldValue: undefined,
          newValue: 'uploaded',
          isHaccpUpload: true
        };
      }
      // No HACCP upload, filter this out
      return null;
    }
    
    return {
      historyId: detail.historyId || detail.HistoryId || 0,
      fieldName: fieldName,
      oldValue: String(oldValue),
      newValue: String(newValue)
    };
  }
  
  private formatAuthorizedBy(value: any, detail?: any): string {
    if (!value || value === 'null' || value === null) return 'N/A';
    
    // Check if we have name mapping in the detail object
    if (detail) {
      // Check for AuthorizerName or other name fields in the detail
      const nameField = detail.authorizerName || detail.AuthorizerName || 
                       detail.authorizedByName || detail.AuthorizedByName;
      if (nameField && typeof value === 'number' || /^\d+$/.test(String(value))) {
        return String(nameField);
      }
    }
    
    // If it's a number (user ID), we need to show a name
    // In production, this would come from the backend with the user name
    if (typeof value === 'number' || /^\d+$/.test(String(value))) {
      // Map common IDs to names for demo purposes
      const userMap: Record<string, string> = {
        '1': 'Sarah Johnson',
        '2': 'John Smith',
        '3': 'Mike Wilson',
        '4': 'Emily Davis',
        '5': 'Robert Brown'
      };
      return userMap[String(value)] || `User #${value}`;
    }
    
    // It's already a name
    return String(value);
  }
  
  private formatOriginField(value: any, detail?: any): string {
    if (!value || value === 'null' || value === null) return 'N/A';
    
    // Check if we have name mapping in the detail object
    if (detail) {
      // Check for OriginName or other name fields in the detail
      const nameField = detail.originName || detail.OriginName || 
                       detail.originFieldName || detail.OriginFieldName;
      if (nameField && (typeof value === 'number' || /^\d+$/.test(String(value)))) {
        return String(nameField);
      }
    }
    
    // If it's a number (field ID), we need to show a field name
    // In production, this would come from the backend with the field name
    if (typeof value === 'number' || /^\d+$/.test(String(value))) {
      // Map common IDs to field names for demo purposes
      const fieldMap: Record<string, string> = {
        '1': 'Field A-123',
        '2': 'Field B-456',
        '3': 'Field C-789',
        '4': 'North Field',
        '5': 'South Field',
        '6': 'East Field',
        '7': 'West Field'
      };
      return fieldMap[String(value)] || `Field #${value}`;
    }
    
    // It's already a field name
    return String(value);
  }

  private getUtcTimestamp(value: string | undefined | null): string | undefined {
    return value ? this.dateTimeHelper.ensureUtcTimestamp(value) : undefined;
  }

  private formatDateTime(value: any): string {
    if (!value || value === 'null' || value === null) return 'N/A';
    
    // Ensure UTC timestamp and format for display
    const utcTimestamp = this.dateTimeHelper.ensureUtcTimestamp(String(value));
    if (!utcTimestamp) return 'N/A';
    
    const date = new Date(utcTimestamp);
    if (!Number.isNaN(date.getTime())) {
      // Format as "dd MMM yyyy, HH:mm" with ordinal
      const day = date.getDate();
      const dayWithOrdinal = this.getDayWithOrdinal(day);
      const formatted = date.toLocaleString('en-GB', {
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      // Replace the day part with ordinal version
      return `${dayWithOrdinal} ${formatted}`.replace(',', ' at');
    }
    
    return String(value);
  }

  private getDayWithOrdinal(day: number): string {
    if (day >= 11 && day <= 13) {
      return `${day}th`;
    }
    const lastDigit = day % 10;
    let suffix: string;
    if (lastDigit === 1) {
      suffix = 'st';
    } else if (lastDigit === 2) {
      suffix = 'nd';
    } else if (lastDigit === 3) {
      suffix = 'rd';
    } else {
      suffix = 'th';
    }
    return `${day}${suffix}`;
  }

  filtered(): HistoryItem[] {
    const q = this.query().trim().toLowerCase();
    let result = this.items();
    
    // Apply search filter if query exists
    if (q) {
      result = result.filter(i =>
        i.action.toLowerCase().includes(q) ||
        i.reason.toLowerCase().includes(q) ||
        i.createdByUsername.toLowerCase().includes(q) ||
        i.createdByRoles.toLowerCase().includes(q)
      );
    }

    // History is already sorted by createdOn descending (latest first) in loadHistory/loadFromOfflineStorage
    // No need to reverse - just return the filtered results
    return result;
  }

  onClose(): void {
    this.closed.emit();
  }

  goBack(): void { history.back(); }

  get headerLoadNumber(): string { return this.header?.loadNumber || ''; }
}


